import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Calendar, 
  Wrench, 
  UserCog, 
  BarChart3, 
  LogOut,
  Menu,
  X,
  Map,
  Settings,
  GitBranch,
  Inbox,
  Route,
  ChevronDown,
  MapPin,
  Layers,
  Package,
} from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@features/auth/hooks/useAuth';
import { InstallAppBanner } from '@components/common/InstallAppBanner';
import { NotificationBell } from '@components/common/NotificationBell';
import { OfflineIndicator } from '@components/common/OfflineIndicator';
import { BottomNavigation } from '@components/mobile';
import { InfoTooltip } from '@components/ui/InfoTooltip';
import { cn } from '@lib/utils';

interface NavChild {
  path: string;
  label: string;
  icon: React.ElementType;
  hint?: string;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  hint?: string;
  children?: NavChild[];
}

const navItems: NavItem[] = [
  {
    path: '/admin/dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    hint: 'Resumen ejecutivo del dia. Citas activas, ingresos del periodo, tecnicos en campo y alertas de cumplimiento COFEPRIS.',
  },
  {
    path: '/admin/clients',
    label: 'Clientes',
    icon: Users,
    hint: 'Directorio de clientes y prospectos. Crea, edita y segmenta contactos; gestiona propiedades vinculadas y consulta el historial completo de servicios.',
  },
  {
    path: '/admin/pipeline',
    label: 'Pipeline',
    icon: GitBranch,
    hint: 'Embudo de ventas Kanban. Mueve prospectos entre etapas (Nuevo Lead › Propuesta › Negociacion › Cerrado) para un seguimiento comercial estructurado.',
  },
  {
    path: '/admin/leads',
    label: 'Leads',
    icon: Inbox,
    hint: 'Bandeja de prospectos sin clasificar. Captura contactos entrantes de cualquier canal antes de ingresarlos al pipeline comercial.',
  },
  {
    path: '/admin/citas',
    label: 'Citas',
    icon: Calendar,
    hint: 'Agenda de servicios. Programa, reagenda o cancela visitas tecnicas; visualiza en lista, calendario o linea de tiempo.',
  },
  {
    path: '/admin/servicios',
    label: 'Servicios',
    icon: Wrench,
    hint: 'Catalogo de tratamientos. Define nombre, descripcion, precio base y duracion de cada servicio ofrecido.',
  },
  {
    path: '/admin/inventario',
    label: 'Inventario',
    icon: Package,
    hint: 'Control de existencias de productos y materiales. Registra plaguicidas, equipos y suministros utilizados en campo.',
  },
  {
    path: '/admin/tecnicos',
    label: 'Tecnicos',
    icon: UserCog,
    hint: 'Directorio de personal tecnico. Gestiona perfiles, especialidades, zonas de cobertura y disponibilidad de cada tecnico.',
  },
  {
    path: '/admin/mapa',
    label: 'Mapa',
    icon: Map,
    hint: 'Visualizacion geografica de propiedades y operaciones activas. Identifica concentracion de clientes y tecnicos en campo.',
    children: [
      {
        path: '/admin/mapa/propiedades',
        label: 'Propiedades',
        icon: MapPin,
        hint: 'Vista geografica de todas las propiedades registradas. Identifica concentracion de clientes por zona.',
      },
      {
        path: '/admin/mapa/operaciones',
        label: 'Operaciones',
        icon: Layers,
        hint: 'Mapa en tiempo real de operaciones activas. Muestra ubicacion de tecnicos y citas del dia.',
      },
    ],
  },
  {
    path: '/admin/rutas',
    label: 'Rutas',
    icon: Route,
    hint: 'Optimizacion de recorridos. Genera la secuencia mas eficiente de visitas para minimizar tiempos de traslado.',
  },
  {
    path: '/admin/reportes',
    label: 'Reportes',
    icon: BarChart3,
    hint: 'Analisis financiero y de desempeno: ingresos, comparativos por periodo, tendencias de plagas y metricas operativas clave.',
  },
  {
    path: '/admin/settings',
    label: 'Configuracion',
    icon: Settings,
    hint: 'Ajustes del sistema. Personaliza datos de empresa, zonas de servicio, roles de usuario y parametros de facturacion.',
  },
];

function NavItemWithChildren({
  item,
  onNavigate,
}: {
  item: NavItem & { children: NavChild[] };
  onNavigate: () => void;
}) {
  const location = useLocation();
  const isParentActive = location.pathname.startsWith(item.path);
  const [open, setOpen] = useState(isParentActive);

  return (
    <div>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'sidebar-link w-full',
          isParentActive && 'sidebar-link-active'
        )}
      >
        <item.icon className="h-5 w-5" />
        <span className="flex-1 text-left">{item.label}</span>
        {item.hint && (
          <InfoTooltip
            content={item.hint}
            position="right"
            className="mr-1"
          />
        )}
        <ChevronDown
          className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div className="ml-4 mt-1 flex flex-col gap-1 border-l border-gray-200 pl-3">
          {item.children.map((child) => (
            <NavLink
              key={child.path}
              to={child.path}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn('sidebar-link text-sm', isActive && 'sidebar-link-active')
              }
            >
              <child.icon className="h-4 w-4" />
              <span className="flex-1">{child.label}</span>
              {child.hint && (
                <InfoTooltip content={child.hint} position="right" />
              )}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 transform bg-white shadow-lg transition-transform duration-300 lg:relative lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center border-b border-gray-100 px-6">
          <img
            src="/logo-merino.png"
            alt="Merino Control de Plagas"
            className="h-10 w-auto object-contain"
          />
        </div>

        {/* Navigation */}
        <nav className="flex flex-col gap-1 overflow-y-auto p-4 pb-32">
          {navItems.map((item) =>
            item.children ? (
              <NavItemWithChildren
                key={item.path}
                item={item as NavItem & { children: NavChild[] }}
                onNavigate={() => setSidebarOpen(false)}
              />
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  cn('sidebar-link', isActive && 'sidebar-link-active')
                }
              >
                <item.icon className="h-5 w-5" />
                <span className="flex-1">{item.label}</span>
                {item.hint && (
                  <InfoTooltip content={item.hint} position="right" />
                )}
              </NavLink>
            )
          )}
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-100 p-4">
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-merino-100 font-semibold text-merino-700">
              {user?.nombre?.charAt(0).toUpperCase() ?? 'U'}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-gray-900">
                {user?.nombre ?? 'Usuario'}
              </p>
              <p className="truncate text-xs text-gray-500">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="sidebar-link w-full text-danger-600 hover:bg-danger-50 hover:text-danger-700"
          >
            <LogOut className="h-5 w-5" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex flex-1 flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-gray-100 bg-white px-4 lg:px-8">
          <button
            onClick={() => setSidebarOpen(true)}
            className="rounded-lg p-2 hover:bg-gray-100 lg:hidden"
          >
            {sidebarOpen ? (
              <X className="h-6 w-6 text-gray-600" />
            ) : (
              <Menu className="h-6 w-6 text-gray-600" />
            )}
          </button>
          
          <div className="flex-1" />

          <NotificationBell />

          <span className="badge-success">
            {user?.rol?.replace('_', ' ').toUpperCase()}
          </span>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 pb-24 lg:p-8 lg:pb-8">
          <Outlet />
        </main>
      </div>

      {/* Bottom Navigation - Only mobile */}
      <BottomNavigation />

      {/* Offline/Sync Indicator */}
      <OfflineIndicator position="top" />

      {/* Install PWA Banner */}
      <InstallAppBanner position="bottom" delayMs={30000} />
    </div>
  );
}
